import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official'; // Or use @highcharts/react components
import 'highcharts/modules/exporting';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { formatNumber } from '../../../../Common/FormatNumber';

interface ChartProps {
    title: string;
    id: number;
    height: number;
 }

export default function ProductsPerMonth(props: ChartProps) {
    const [data, setData] = React.useState<any>(null);
    const [categories, setCategories] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const chartComponentRef = React.useRef<HighchartsReact.RefObject>(null);
    React.useEffect(() => {
        const fetchData = async (product_grp_1: string) =>    {
            try {
                setLoading(true);
                let response: Response;
                if (product_grp_1) {
                    response = await fetch(`/dashboard/products-per-month/${props.id}/?entity_id=${localStorage.getItem('entity_id')}&product_grp_1=${product_grp_1}`);
                } else {
                    response = await fetch(`/dashboard/products-per-month/${props.id}/?entity_id=${localStorage.getItem('entity_id')}`);
                }
                if (!response.ok) {
                    throw new Error('Failed to fetch data');
                }
                const data = await response.json();
                setData(data);
                setCategories(data.categories);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData(data?.product_grp_1);

        const handleResize = () => {
        if (chartComponentRef.current && chartComponentRef.current.chart) {
            chartComponentRef.current.chart.reflow();
        }
        };

        window.addEventListener('resize', handleResize);
        return () => {
        window.removeEventListener('resize', handleResize);
        };
    }, [data?.product_grp_1]);
  
  const options: Highcharts.Options = {
    chart: {
      style: {
        fontFamily: 'inherit',
      },
      zooming: {
        type: 'xy' as const
      }
    },
    title: {
        text: props.title,
        align: 'left'
    },

    yAxis: {
        labels: {
            formatter: function(this: Highcharts.AxisLabelsFormatterContextObject) {
                const v = this.value as number;
                const absv = Math.abs(v);
                if (absv >= 1000000) return v / 1000000 + 'M';
                if (absv >= 1000) return v / 1000 + 'K';
                return String(v);
            },
            style: {
                fontFamily: 'Trebuchet MS'
            }
        },
        title: {
            text: 'Sales ($)',
            style: {
                fontFamily: 'Trebuchet MS'
            }
        },
    },

    xAxis: {
        categories: categories,
        labels: {
            style: {
                fontFamily: 'Trebuchet MS'
            }
        }
    },

    // legend: {
    //     layout: 'vertical',
    //     align: 'right',
    //     verticalAlign: 'middle'
    // },

    plotOptions: {
        series: {
            label: {
                connectorAllowed: false
            },
            // pointStart: 2010
        },
    },

    tooltip: {
        formatter: function(this: Highcharts.Point) {
          return `<b>${this.series.name}</b><br><span style="font-family: Trebuchet MS;">${this.category}: ${Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.y)}</span>`;
        }
    },

    exporting: {
        enabled: true, // Enable the export menu
        buttons: {
            contextButton: {
                menuItems: [
                    'downloadPNG',
                    'downloadJPEG',
                    'downloadPDF',
                    'downloadSVG',
                    'separator',
                    'printChart',
                ],
            },
        },
    },

    series: [
      {
        type: 'line', // Specify the chart type
        name: 'Monthly Sales',
        data: loading ? [] : data && data.sales.product_1 || []
      },
    ],
  };

  return (
    <div style={{ height: '100%', width: '100%' }}>
        <Box sx={{ height: '25%', width: '100%', display: 'flex', justifyContent: 'center', flexGrow: 1, gap: 3, paddingTop: 3 }}>
            <TextField
                size="small"
                variant="standard"
                sx={{ width: '100%' }}
                id="product_grp_1"
                select
                label="Product"
                value={data && data.product_grp_1 || ''}
                onChange={(event) => setData({ ...data, product_grp_1: event.target.value })}
            >
                {data && data.product_grps_1.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
            </TextField>
            
        </Box>
        <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', flexGrow: 1 }}>
            {data && data.product_grp_1 ? (
                <HighchartsReact
                highcharts={Highcharts}
                options={options}
                allowChartUpdate={true}
                exportEnabled={true}
                ref={chartComponentRef}
                containerProps={{ style: { width: '100%', height: props.height, border: 'none', borderRadius: 5, boxShadow: 'none', margin: 0, padding: 0 } }}
                />
            ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>No data available</Typography>
            )}
        </Box>
    </div>
  );
}